/*
 * @copyright Copyright (c) valantic CEC Deutschland GmbH (https://www.valantic.com)
 * @see PROJECT_LICENSE.txt
 */

/**
 * Extracts image URLs from a specific item attribute
 *
 * This function retrieves and parses image data from a designated attribute (`mey_livesearch_images`)
 * on the provided `item` object. It then extracts and returns URLs for different image sizes:
 * small, large, and thumbnail.
 *
 * @param {Object} item - The item object containing attribute data.
 * @returns {Object} - An object containing image URLs in the following structure:
 * {
 *   small: {label: string, url: string} | {code: string, label: string, name: string} | any, // The small image data or null if not found
 *   large: string | any,        // The URL or reference for the large image, or null if not found
 *   thumbnail: string | any     // The URL or reference for the thumbnail image. If not directly found, falls back to small image
 * }
 *
 * Logic:
 * 1. Get the raw attribute value from the item using `getAttributeValue`.
 * 2. Parse the JSON string to get an array of image objects.
 * 3. Attempt to extract:
 *    - `smallImage` from the first object with a `small_image` key.
 *    - `largeImage` from the first object with an `image` key.
 *    - `thumbnail` from the first object with a `thumbnail` key,
 *       falling back to the `smallImage` if not found.
 * 4. Return an object containing the selected image values.
 */
const getImagesFromAttribute = (item) => {
    const imagesArray = JSON.parse(getAttributeValue(item, "mey_livesearch_images")) || null;

    const smallImage = imagesArray?.find(img => img.small_image)?.small_image || null;
    const largeImage = imagesArray?.find(img => img.image)?.image || null;
    const thumbnail = imagesArray?.find(img => img.thumbnail)?.image || smallImage || null;

    return {
        small: smallImage,
        large:  largeImage,
        thumbnail:  thumbnail,
    };
};

/**
 * Get image label from productView or use product name as fallback
 * @param item
 * @returns {*}
 */
const getImageLabel = (item) => {
    return item.productView.images.length > 0 ? item.productView.images[0].label : item.product.name;
};

/**
 * Check if overlayer attribute is set
 *
 * @param item
 * @param name
 * @returns {*}
 */
const overlayerAttributeIsSet = (item, name) => {
    return item.productView?.attributes?.some(attr => attr.name === name && attr.value === "yes");
};

/**
 * Get value of an attribute
 *
 * @param item
 * @param attribute
 * @returns {*}
 */
const getAttributeValue = (item, attribute) => {
    return item.productView?.attributes?.find(attr => attr.name === attribute)?.value || null;
};

/**
 * Get fake swatches from item attribute
 *
 * @param item
 * @returns {any[]|null}
 */
const getFakeSwatches = (item) => {
    const attrValue = JSON.parse(getAttributeValue(item, "mey_fake_swatch")) || null;
    return Array.isArray(attrValue) ? attrValue : null;
};

/**
 * Map item to Google Tag Manager data layer format
 *
 * @param item
 * @returns {{is_sale: *, item_color: *, item_design_color_select: *, item_id, item_master_number: *, item_name, item_overlayer_new: *, item_size: string, item_sku: (string|*), item_style: *, magento_id, magento_sku: (string|*), price: (*|number)}}
 */
const mapGtmItem = (item) => {
    return {
        is_sale: overlayerAttributeIsSet(item, "overlayer_sale"),
        item_color: getAttributeValue(item, "color"),
        item_design_color_select: getAttributeValue(item, "design_color_select"),
        item_id: item.product.id,
        item_master_number: getAttributeValue(item, "master_number"),
        item_name: item.product.name,
        item_overlayer_new: overlayerAttributeIsSet(item, "overlayer_new"),
        item_size: item.product.size || "",
        item_sku: item.product.sku,
        item_style: getAttributeValue(item, "style"),
        magento_id: item.product.id,
        magento_sku: item.product.sku,
        price: item.product.price_range.maximum_price.regular_price.value || 0
    }
}

export {
    getImagesFromAttribute,
    getImageLabel,
    overlayerAttributeIsSet,
    getAttributeValue,
    getFakeSwatches,
    mapGtmItem
};
